import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/utils/cn';
import { Icon, LoadingIcon } from '@/primer/components';
import type { IconName } from '@/primer/components';
import Typography from '@/primer/components/Typography/Typography';
import type { TypographyColor } from '@/primer/components/Typography/Typography';

export type SocialAuthProvider = 'Facebook' | 'Google' | 'Apple';

export interface SocialAuthButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  provider: SocialAuthProvider;
  /** Button label. Defaults to `Continue with <provider>`. */
  label?: ReactNode;
  /** Which side the brand mark sits on. @default 'left' */
  iconPosition?: 'left' | 'right';
  /** Hides the brand mark — used while a redirect is in flight. */
  hideIcon?: boolean;
  isLoading?: boolean;
}

type SocialAuthProviderConfig = {
  styles: string;
  labelColor: TypographyColor;
  icon: IconName;
};

// TODO: Replace temporary brand values with Primer social tokens when available.
const providerConfig: Record<SocialAuthProvider, SocialAuthProviderConfig> = {
  Facebook: {
    styles: 'bg-[#5890FF] hover:bg-[#4a7de8]',
    labelColor: 'white',
    icon: 'facebook-creator-auth',
  },
  Apple: {
    styles: 'bg-[#000000] hover:bg-[#1a1a1a]',
    labelColor: 'white',
    icon: 'apple-creator-auth',
  },
  Google: {
    styles: 'bg-surface border border-default hover:bg-page',
    labelColor: 'header',
    icon: 'google-creator-auth',
  },
};

export function SocialAuthButton({
  provider,
  label,
  iconPosition = 'left',
  hideIcon = false,
  isLoading = false,
  className,
  ...props
}: SocialAuthButtonProps) {
  const config = providerConfig[provider];
  const icon = isLoading ? (
    <div
      aria-hidden="true"
      className={config.labelColor === 'white' ? 'text-white' : 'text-header'}
    >
      <LoadingIcon size="large" />
    </div>
  ) : hideIcon ? null : (
    <Icon name={config.icon} size="large" className="shrink-0" />
  );

  return (
    <button
      type="button"
      className={cn(
        'w-full flex items-center justify-center gap-3 px-6 py-4 rounded-medium',
        config.styles,
        'transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed',
        className
      )}
      {...props}
      disabled={props.disabled || isLoading}
      aria-busy={isLoading || undefined}
    >
      {iconPosition === 'left' && icon}
      <Typography variant="button" size="large" color={config.labelColor}>
        {label ?? `Continue with ${provider}`}
      </Typography>
      {iconPosition === 'right' && icon}
    </button>
  );
}

export default SocialAuthButton;
